/**
 * NUEVO: frontend/src/pages/Profile.js
 * Agregar en App.js: <Route path="/profile" element={<Protected><Profile /></Protected>} />
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Navbar, Btn, C } from "../components/UI";

const MODELS = [
  { id:"claude-sonnet-4-6", label:"Sonnet 4.6", desc:"Más inteligente — mejor para tareas complejas", cost:"3 créditos/mensaje", icon:"🧠" },
  { id:"claude-haiku-4-5-20251001", label:"Haiku 4.5", desc:"Más rápido — ideal para tareas simples", cost:"1 crédito/mensaje", icon:"⚡" },
];

const CREDIT_PACKAGES = [
  { id:"starter", credits:500,  price:"$5.000 ARS", label:"Starter",  icon:"🌱" },
  { id:"pro",     credits:1500, price:"$12.000 ARS", label:"Pro",      icon:"🚀", highlighted:true },
  { id:"power",   credits:4000, price:"$25.000 ARS", label:"Power",    icon:"⚡" },
];

export default function Profile() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState("knowledge");
  const [buyingPkg, setBuyingPkg] = useState(null);

  useEffect(() => {
    Promise.all([
      apiFetch("/profile"),
      apiFetch("/credits/history"),
    ]).then(([p, h]) => { setProfile(p); setHistory(h); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/profile", { method:"PUT", body:JSON.stringify({
        user_knowledge: profile.user_knowledge,
        preferred_model: profile.preferred_model,
        custom_instructions: profile.custom_instructions,
      })});
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const buyCredits = async (pkg) => {
    setBuyingPkg(pkg.id);
    try {
      const { init_point } = await apiFetch("/mp/create-preference", {
        method:"POST", body:JSON.stringify({ package_id: pkg.id })
      });
      window.open(init_point, "_blank");
    } catch(e) {
      if (e.message.includes("not configured")) {
        alert("MercadoPago no está configurado todavía. Contactá al admin para recargar créditos.");
      } else {
        alert(e.message);
      }
    }
    setBuyingPkg(null);
  };

  if (!profile) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:C.muted }}>Cargando…</div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", flexDirection:"column" }}>
      <Navbar />
      <div style={{ maxWidth:"800px", margin:"0 auto", padding:"2rem 1.5rem", width:"100%" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:"2rem" }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:C.accent,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontWeight:800, fontSize:"1.2rem", color:"#0d0d0f", marginRight:"1rem" }}>
            {profile.username?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:"1.1rem" }}>{profile.username}</div>
            <div style={{ color:C.muted, fontSize:"0.82rem" }}>{profile.email}</div>
          </div>
          <div style={{ marginLeft:"auto", textAlign:"right" }}>
            <div style={{ fontSize:"1.8rem", fontWeight:800, color:C.accent }}>{profile.credits}</div>
            <div style={{ color:C.muted, fontSize:"0.72rem" }}>créditos disponibles</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:"1.5rem" }}>
          {[
            ["knowledge","🧠 Mi conocimiento"],
            ["model","⚡ Modelo de IA"],
            ["credits","💳 Créditos"],
          ].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ background:"none", border:"none", color:tab===id?C.accent:C.muted,
                fontWeight:tab===id?600:400, fontSize:"0.83rem",
                padding:"9px 18px", borderBottom:tab===id?`2px solid ${C.accent}`:"2px solid transparent",
                marginBottom:"-1px", cursor:"pointer" }}>{label}</button>
          ))}
        </div>

        {/* Knowledge tab */}
        {tab === "knowledge" && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"1.5rem" }}>
              <div style={{ fontWeight:600, marginBottom:"6px" }}>Mi conocimiento</div>
              <div style={{ color:C.muted, fontSize:"0.82rem", marginBottom:"1rem", lineHeight:1.6 }}>
                Contale al agente sobre vos: tu trabajo, tus clientes, tu metodología, tus preferencias.
                El agente va a usar esta info en todas tus conversaciones.
              </div>
              <textarea
                value={profile.user_knowledge || ""}
                onChange={e => setProfile(p => ({...p, user_knowledge: e.target.value}))}
                rows={8}
                placeholder={`Ejemplos:\n- Soy consultor de MercadoLibre especializado en pymes argentinas\n- Mis clientes venden principalmente textiles y electrónica\n- Prefiero código limpio y bien comentado\n- Siempre uso Bootstrap para el CSS\n- Mi empresa se llama TechConsult`}
                style={{ width:"100%", background:"#0d0d0f", border:`1px solid ${C.border}`, color:C.text,
                  borderRadius:"8px", padding:"12px", fontSize:"0.85rem", lineHeight:1.6,
                  resize:"vertical", fontFamily:"system-ui" }}
              />
            </div>

            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", padding:"1.5rem" }}>
              <div style={{ fontWeight:600, marginBottom:"6px" }}>Instrucciones personalizadas</div>
              <div style={{ color:C.muted, fontSize:"0.82rem", marginBottom:"1rem", lineHeight:1.6 }}>
                Reglas que el agente debe seguir siempre. Por ejemplo: idioma, estilo de código, frameworks preferidos.
              </div>
              <textarea
                value={profile.custom_instructions || ""}
                onChange={e => setProfile(p => ({...p, custom_instructions: e.target.value}))}
                rows={4}
                placeholder={`Ejemplos:\n- Siempre respondé en español\n- Usá siempre Bootstrap para el CSS\n- Prefiero SQLite sobre PostgreSQL para proyectos pequeños`}
                style={{ width:"100%", background:"#0d0d0f", border:`1px solid ${C.border}`, color:C.text,
                  borderRadius:"8px", padding:"12px", fontSize:"0.85rem", lineHeight:1.6,
                  resize:"vertical", fontFamily:"system-ui" }}
              />
            </div>

            <Btn onClick={save} loading={saving} variant={saved?"success":"primary"}>
              {saved ? "✓ Guardado" : "Guardar cambios"}
            </Btn>
          </div>
        )}

        {/* Model tab */}
        {tab === "model" && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div style={{ color:C.muted, fontSize:"0.83rem", marginBottom:"0.5rem" }}>
              Elegí el modelo que usa el agente en tus proyectos. Podés cambiarlo en cualquier momento.
            </div>
            {MODELS.map(m => (
              <div key={m.id} onClick={() => setProfile(p => ({...p, preferred_model: m.id}))}
                style={{ padding:"1.5rem", background:C.surface,
                  border:`2px solid ${profile.preferred_model === m.id ? C.accent : C.border}`,
                  borderRadius:"12px", cursor:"pointer",
                  background: profile.preferred_model === m.id ? "#1e1c00" : C.surface }}>
                <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                  <span style={{ fontSize:"1.8rem" }}>{m.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color: profile.preferred_model === m.id ? C.accent : C.text }}>
                      {m.label}
                    </div>
                    <div style={{ color:C.muted, fontSize:"0.82rem", marginTop:"2px" }}>{m.desc}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:"0.78rem", color:C.muted, background:"#1a1a22",
                      border:`1px solid ${C.border}`, borderRadius:"4px", padding:"3px 10px" }}>
                      {m.cost}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Btn onClick={save} loading={saving} variant={saved?"success":"primary"}>
              {saved ? "✓ Guardado" : "Guardar preferencia"}
            </Btn>
          </div>
        )}

        {/* Credits tab */}
        {tab === "credits" && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>

            {/* Balance */}
            <div style={{ background:"#0a1a0a", border:`1px solid ${C.success}33`,
              borderRadius:"12px", padding:"1.5rem", display:"flex", alignItems:"center", gap:"1.5rem" }}>
              <div style={{ fontSize:"3rem", fontWeight:800, color:C.success }}>{profile.credits}</div>
              <div>
                <div style={{ fontWeight:600 }}>Créditos disponibles</div>
                <div style={{ color:C.muted, fontSize:"0.82rem" }}>Usados: {profile.credits_used} créditos</div>
              </div>
            </div>

            {/* Packages */}
            <div>
              <div style={{ fontWeight:600, marginBottom:"1rem" }}>Recargar créditos</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"1rem" }}>
                {CREDIT_PACKAGES.map(pkg => (
                  <div key={pkg.id} style={{ background: pkg.highlighted ? "#1a1a00" : C.surface,
                    border:`2px solid ${pkg.highlighted ? C.accent : C.border}`,
                    borderRadius:"12px", padding:"1.5rem", textAlign:"center", position:"relative" }}>
                    {pkg.highlighted && (
                      <div style={{ position:"absolute", top:"-12px", left:"50%", transform:"translateX(-50%)",
                        background:C.accent, color:"#0d0d0f", borderRadius:"20px", padding:"2px 12px",
                        fontSize:"0.68rem", fontWeight:700 }}>Popular</div>
                    )}
                    <div style={{ fontSize:"1.5rem", marginBottom:"6px" }}>{pkg.icon}</div>
                    <div style={{ fontWeight:700, marginBottom:"4px" }}>{pkg.label}</div>
                    <div style={{ fontSize:"1.5rem", fontWeight:800, color:C.accent, marginBottom:"4px" }}>
                      {pkg.credits}
                    </div>
                    <div style={{ color:C.muted, fontSize:"0.72rem", marginBottom:"1rem" }}>créditos</div>
                    <div style={{ fontWeight:600, marginBottom:"1rem" }}>{pkg.price}</div>
                    <Btn onClick={() => buyCredits(pkg)} loading={buyingPkg === pkg.id} small>
                      Comprar
                    </Btn>
                  </div>
                ))}
              </div>
            </div>

            {/* History */}
            {history.length > 0 && (
              <div>
                <div style={{ fontWeight:600, marginBottom:"1rem" }}>Historial de movimientos</div>
                <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"12px", overflow:"hidden" }}>
                  {history.slice(0,10).map((h, i) => (
                    <div key={h.id} style={{ display:"flex", alignItems:"center", padding:"12px 16px",
                      borderBottom: i < Math.min(history.length,10)-1 ? `1px solid ${C.border}` : "none" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:"0.83rem" }}>{h.description}</div>
                        <div style={{ color:C.muted, fontSize:"0.72rem" }}>
                          {new Date(h.created_at).toLocaleString("es-AR")}
                        </div>
                      </div>
                      <div style={{ fontWeight:700, color: h.amount > 0 ? C.success : C.error }}>
                        {h.amount > 0 ? "+" : ""}{h.amount}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
